import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaFingerprint,
  FaHome,
  FaKeyboard,
  FaMobileAlt,
  FaPlug,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
  FaSyncAlt,
  FaTimesCircle,
  FaUserClock,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import CalendarView from "../ChildComponent/CalendarView";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function startOfDay(date = new Date()) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Created By Cursor
function formatIso(date) {
  const d = startOfDay(date);
  const day = d.getDate();
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const mm = month > 9 ? String(month) : `0${month}`;
  const dd = day > 9 ? String(day) : `0${day}`;
  return {
    date: d,
    iso: `${year}-${mm}-${dd}`,
    display: `${dd}-${mm}-${year}`,
    month: mm,
    year,
  };
}

// Created By Cursor
function staffInitials(name) {
  const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
}

// Created By Cursor
function normalizeMark(status) {
  const value = String(status || "").trim();
  if (!value || value === "Not Taken") return "";
  return value;
}

function displayMark(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "Present";
  if (value === "A" || value === "Absent") return "Absent";
  if (value === "L" || value === "Leave") return "Leave";
  if (value === "HD" || value === "Half Day") return "Half Day";
  if (value === "LA" || value === "Late Attendance") return "Late Attendance";
  return value;
}

function markTone(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "p";
  if (value === "A" || value === "Absent") return "a";
  if (value === "L" || value === "Leave") return "l";
  if (value === "HD" || value === "Half Day") return "hd";
  if (value === "LA" || value === "Late Attendance") return "la";
  return "pending";
}

function markCode(status) {
  const value = normalizeMark(status);
  if (value === "P" || value === "Present") return "P";
  if (value === "A" || value === "Absent") return "A";
  if (value === "L" || value === "Leave") return "L";
  if (value === "HD" || value === "Half Day") return "HD";
  if (value === "LA" || value === "Late Attendance") return "LA";
  return "";
}

function staffField(staff, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = String(staff?.[keys[i]] || "").trim();
    if (value) return value;
  }
  return "";
}

function formatChipDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const iso = raw.slice(0, 10);
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return raw;
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${Number(match[3])} ${months[Number(match[2]) - 1]} ${match[1]}`;
}

function formatChipTime(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const timePart = raw.includes(" ") ? raw.split(" ").pop() : raw;
  const pieces = String(timePart).split(":");
  const hour = Number(pieces[0]);
  const minute = pieces[1] || "00";
  if (!Number.isFinite(hour)) return String(timePart).slice(0, 5);
  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${suffix}`;
}

function displayPunchType(value) {
  const punch = String(value || "").trim().toUpperCase();
  if (punch === "IN") return "In";
  if (punch === "OUT") return "Out";
  return punch;
}

function displaySource(value) {
  const source = String(value || "").trim().toUpperCase();
  if (source === "MANUAL") return "Manual";
  if (source === "BIOMETRIC") return "Biometric";
  if (source === "MOBILE") return "Mobile";
  if (source === "API") return "API";
  return source;
}

function sourceIcon(value) {
  const source = String(value || "").trim().toUpperCase();
  if (source === "BIOMETRIC") return FaFingerprint;
  if (source === "MOBILE") return FaMobileAlt;
  if (source === "API") return FaPlug;
  return FaKeyboard;
}

function isLateMark(status) {
  const value = normalizeMark(status);
  return value === "LA" || value === "Late Attendance";
}

const MARK_CHIPS = [
  { code: "P", title: "Present" },
  { code: "A", title: "Absent" },
  { code: "L", title: "Leave" },
  { code: "LA", title: "Late Attendance" },
  { code: "HD", title: "Half Day" },
];

function stampNow(isoDate) {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const attendanceTime = `${hh}:${mm}:${ss}`;
  return {
    attendance_time: attendanceTime,
    attendance_datetime: `${isoDate} ${attendanceTime}`,
  };
}

function KpiChip({ tone, icon: Icon, label, value }) {
  return (
    <div className={`sm-kpi-chip sm-kpi-chip--${tone}`}>
      <span className="sm-kpi-chip__icon" aria-hidden>
        {Icon ? <Icon /> : null}
      </span>
      <div className="sm-kpi-chip__text">
        <span className="sm-kpi-chip__label">{label}</span>
        <span className="sm-kpi-chip__value">{value}</span>
      </div>
    </div>
  );
}

// Created By Cursor
export default function StaffMonthAttendance() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [monthData, setMonthData] = useState([]);
  const [pendingMarks, setPendingMarks] = useState({});
  const [staffQuery, setStaffQuery] = useState("");

  const monthParts = useMemo(() => formatIso(currentDate), [currentDate]);
  const selectedParts = useMemo(() => formatIso(selectedDate), [selectedDate]);

  const sessionBounds = useMemo(() => {
    const session = SessionManager.getCurrentSession()?.[0] || {};
    return {
      start: session.START_DATE || session.start_date || "",
      end: session.END_DATE || session.end_date || "",
    };
  }, []);

  const selectedDay = useMemo(
    () =>
      monthData.find((d) => d.attendance_date === selectedParts.iso) || null,
    [monthData, selectedParts.iso]
  );

  const loadMonth = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      setMonthData([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaffAttendanceOfMonthYear.php`, {
        params: {
          school_code: schoolCode,
          month: monthParts.month,
          year: monthParts.year,
        },
      });
      if (res.data?.api_status === "OK") {
        setMonthData(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setMonthData([]);
        myToast.notify_warning(res.data?.api_remark || "Could not load attendance.");
      }
    } catch (e) {
      setMonthData([]);
      myToast.notify_warning(
        e?.response?.data?.api_remark || e?.message || "Network error loading attendance."
      );
    } finally {
      setLoading(false);
    }
  }, [monthParts.month, monthParts.year]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    setStaffQuery("");
  }, [selectedParts.iso]);

  useEffect(() => {
    const pendingIds = (selectedDay?.staff || [])
      .filter((s) => !normalizeMark(s.attendance_status))
      .map((s) => s.staff_id)
      .filter(Boolean);
    setPendingMarks((prev) => {
      const next = {};
      pendingIds.forEach((id) => {
        next[id] = prev[id] || "P";
      });
      return next;
    });
  }, [selectedDay]);

  const events = useMemo(
    () =>
      monthData.map((item) => ({
        DATE: item.attendance_date,
        STATUS: item.status,
      })),
    [monthData]
  );

  const stats = useMemo(() => {
    const staff = selectedDay?.staff || [];
    let present = 0;
    let absent = 0;
    let leave = 0;
    let halfDay = 0;
    let late = 0;
    staff.forEach((s) => {
      const mark = normalizeMark(s.attendance_status);
      if (mark === "Present" || mark === "P") present += 1;
      else if (mark === "Absent" || mark === "A") absent += 1;
      else if (mark === "Leave" || mark === "L") leave += 1;
      else if (mark === "Half Day" || mark === "HD") halfDay += 1;
      else if (isLateMark(mark)) late += 1;
    });
    const marked = present + absent + leave + halfDay + late;
    return {
      total: staff.length,
      present,
      absent,
      leave,
      halfDay,
      late,
      marked,
      pending: Math.max(0, staff.length - marked),
      taken: selectedDay?.status === "Taken" || marked > 0,
      offDay: selectedDay?.status === "Holiday",
      dayStatus: selectedDay?.status || "Not Taken",
    };
  }, [selectedDay]);

  const groupedStaff = useMemo(() => {
    const staff = selectedDay?.staff || [];
    const q = staffQuery.trim().toLowerCase();
    const recorded = [];
    const notRecorded = [];
    staff.forEach((s) => {
      if (q) {
        const hay = `${s.staff_name || ""} ${s.staff_id || ""} ${s.staff_role || ""} ${s.staff_gender || ""}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      if (normalizeMark(s.attendance_status)) recorded.push(s);
      else notRecorded.push(s);
    });
    return { recorded, notRecorded, query: q };
  }, [selectedDay, staffQuery]);

  const handlePrevMonth = () => {
    const prev = new Date(currentDate);
    prev.setMonth(prev.getMonth() - 1);
    prev.setDate(1);
    setCurrentDate(prev);
    setSelectedDate(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(currentDate);
    next.setMonth(next.getMonth() + 1);
    next.setDate(1);
    setCurrentDate(next);
    setSelectedDate(next);
  };

  const onDayPress = (date) => {
    const picked = startOfDay(date);
    if (picked > startOfDay(new Date())) {
      myToast.notify_warning("Future dates are not allowed.");
      return;
    }
    setSelectedDate(picked);
  };

  const setPendingMark = (staffId, code) => {
    setPendingMarks((prev) => ({ ...prev, [staffId]: code }));
  };

  const recordOneStaff = async (staff) => {
    const mark = pendingMarks[staff.staff_id] || "P";
    const schoolCode = SessionManager.getSchoolCode();
    const authorizedId = SessionManager.getStaffDetails()?.staff_id;
    if (!schoolCode || !authorizedId || !staff?.staff_id) {
      myToast.notify_warning("Missing school or staff details.");
      return;
    }
    const ok = window.confirm(
      `Record ${staff.staff_name || staff.staff_id} as ${displayMark(mark)}?`
    );
    if (!ok) return;

    setSaving(true);
    try {
      const stamp = stampNow(selectedParts.iso);
      const response = await axios.get(`${API_BASE()}AddStaffAttendance.php`, {
        params: {
          school_code: schoolCode,
          authorized_staff_id: authorizedId,
          staff_id: staff.staff_id,
          attendance_date: selectedParts.iso,
          attendance: mark,
          ...stamp,
        },
      });
      if (response.data?.api_status === "OK") {
        myToast.notify_success("Attendance recorded.");
        await loadMonth();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not record attendance."
        );
      }
    } catch (err) {
      myToast.notify_error("Could not record attendance.");
    } finally {
      setSaving(false);
    }
  };

  const recordAllPending = async () => {
    const list = groupedStaff.notRecorded;
    if (!list.length) {
      myToast.notify_warning("No pending staff to record.");
      return;
    }
    const schoolCode = SessionManager.getSchoolCode();
    const authorizedId = SessionManager.getStaffDetails()?.staff_id;
    if (!schoolCode || !authorizedId) {
      myToast.notify_warning("Missing school or staff details.");
      return;
    }
    const ok = window.confirm(
      `Record all ${list.length} pending staff for ${selectedParts.display}?`
    );
    if (!ok) return;

    const staff_list = JSON.stringify(
      list.map((s) => ({
        staff_id: s.staff_id,
        attendance_status: pendingMarks[s.staff_id] || "P",
      }))
    );

    setSaving(true);
    try {
      const response = await axios.get(`${API_BASE()}AddAllStaffAttendance.php`, {
        params: {
          school_code: schoolCode,
          authorized_staff_id: authorizedId,
          attendance_date: selectedParts.iso,
          staff_list,
        },
      });
      if (response.data?.api_status === "OK") {
        myToast.notify_success("All pending staff recorded.");
        await loadMonth();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not record all staff."
        );
      }
    } catch (err) {
      myToast.notify_error("Could not record all staff.");
    } finally {
      setSaving(false);
    }
  };

  const renderPendingRow = (s, index) => {
    const mark = pendingMarks[s.staff_id] || "P";
    const role = staffField(s, "staff_role");
    const gender = staffField(s, "staff_gender");
    return (
      <div key={s.staff_id || index} className="sm-att-pending-row">
        <div className="sm-att-staff-row__photo-wrap is-pending" aria-hidden>
          {s.staff_image_path ? (
            <img
              className="sm-att-staff-row__photo"
              src={s.staff_image_path}
              alt=""
              onError={(event) => {
                event.currentTarget.style.display = "none";
                const fallback = event.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "inline-flex";
              }}
            />
          ) : null}
          <span
            className="sm-att-staff-row__avatar"
            style={s.staff_image_path ? { display: "none" } : undefined}
          >
            {staffInitials(s.staff_name)}
          </span>
        </div>
        <div className="sm-att-staff-row__body">
          <strong>{s.staff_name}</strong>
          <div className="sm-att-staff-row__meta">
            {s.staff_id ? (
              <span className="sm-att-staff-row__id">{s.staff_id}</span>
            ) : null}
            {gender ? <span className="sm-att-staff-row__chip">{gender}</span> : null}
            {role ? <span>{role}</span> : null}
          </div>
        </div>
        <div className="sm-att-pending-row__marks" role="group" aria-label="Attendance mark">
          {MARK_CHIPS.map((chip) => (
            <button
              key={chip.code}
              type="button"
              className={`tat-mark-pill tat-mark-pill--${chip.code.toLowerCase()}${
                mark === chip.code ? " is-active" : ""
              }`}
              title={chip.title}
              disabled={saving}
              onClick={() => setPendingMark(s.staff_id, chip.code)}
            >
              {chip.code}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="sm-att-record-btn sm-att-record-btn--row"
          disabled={saving}
          onClick={() => recordOneStaff(s)}
        >
          <FaClipboardCheck aria-hidden /> Record
        </button>
      </div>
    );
  };

  const renderStaffRow = (s) => {
    const savedMark = normalizeMark(s.attendance_status);
    const tone = markTone(savedMark);
    const role = staffField(s, "staff_role");
    const gender = staffField(s, "staff_gender");
    const punchType = displayPunchType(staffField(s, "punch_type"));
    const punchCode = String(staffField(s, "punch_type")).toUpperCase();
    const sourceCode = String(staffField(s, "source")).toUpperCase();
    const sourceLabel = displaySource(staffField(s, "source"));
    const SourceIcon = sourceIcon(sourceCode);
    const recordedDate =
      formatChipDate(staffField(s, "attendance_date")) ||
      formatChipDate(selectedDay?.attendance_date || selectedParts.iso);
    const recordedTime =
      formatChipTime(staffField(s, "attendance_time")) ||
      formatChipTime(staffField(s, "attendance_datetime"));
    return (
      <div
        key={`${s.staff_id}-${punchCode || ""}-${staffField(s, "attendance_time")}`}
        className={`sm-att-staff-card${savedMark ? ` is-${tone}` : " is-pending"}`}
      >
        <div className="sm-att-staff-card__head">
          <div className={`sm-att-staff-row__photo-wrap is-${tone}`} aria-hidden>
            {s.staff_image_path ? (
              <img
                className="sm-att-staff-row__photo"
                src={s.staff_image_path}
                alt=""
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                  const fallback = event.currentTarget.nextElementSibling;
                  if (fallback) fallback.style.display = "inline-flex";
                }}
              />
            ) : null}
            <span
              className="sm-att-staff-row__avatar"
              style={s.staff_image_path ? { display: "none" } : undefined}
            >
              {staffInitials(s.staff_name)}
            </span>
          </div>
          <div className="sm-att-staff-row__body">
            <strong>{s.staff_name}</strong>
            <div className="sm-att-staff-row__meta">
              {s.staff_id ? (
                <span className="sm-att-staff-row__id">{s.staff_id}</span>
              ) : null}
              {gender ? <span className="sm-att-staff-row__chip">{gender}</span> : null}
              {role ? <span>{role}</span> : null}
            </div>
          </div>
          {savedMark ? (
            <span className={`sm-att-staff-row__badge is-${tone}`}>
              <em>{markCode(savedMark)}</em>
              {displayMark(savedMark)}
            </span>
          ) : (
            <span className="sm-att-staff-row__badge is-pending">Not recorded</span>
          )}
        </div>
        {savedMark ? (
          <div className="sm-att-staff-row__facts">
            {punchType ? (
              <span
                className={`sm-att-fact is-punch-${punchCode === "OUT" ? "out" : "in"}`}
                title="Punch type"
              >
                {punchCode === "OUT" ? <FaSignOutAlt aria-hidden /> : <FaSignInAlt aria-hidden />}
                {punchType}
              </span>
            ) : null}
            {sourceLabel ? (
              <span
                className={`sm-att-fact is-source-${sourceCode.toLowerCase() || "manual"}`}
                title="Source"
              >
                <SourceIcon aria-hidden />
                {sourceLabel}
              </span>
            ) : null}
            {recordedDate ? (
              <span className="sm-att-fact is-date" title="Attendance date">
                <FaCalendarAlt aria-hidden />
                {recordedDate}
              </span>
            ) : null}
            {recordedTime ? (
              <span className="sm-att-fact is-time" title="Attendance time">
                <FaClock aria-hidden />
                {recordedTime}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const monthLabel = currentDate.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page mat-page tat-page atd-page"
      style={{ fontFamily: PAGE_FONT, background: "#ffffff" }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <Link to="/AdminMain/StaffManagement" style={{ color: COLORS.white }}>
                Staff Management
              </Link>
            </li>
            <li>
              <Link to="/AdminMain/StaffAttendanceDash" style={{ color: COLORS.white }}>
                Staff Attendance
              </Link>
            </li>
            <li>
              <span style={{ color: COLORS.white }}> Month</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell mat-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCalendarAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Month Attendance</h1>
              <p className="sm-hero__sub">
                Staff calendar · {monthLabel}
                {stats.dayStatus ? ` · ${stats.dayStatus}` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <Link to="/AdminMain/StaffAttendanceDash" className="tat-hero-link">
              <FaClipboardCheck aria-hidden />
              <span>Dashboard</span>
            </Link>
            <button
              type="button"
              className={`pp-icon-btn${loading ? " spinning" : ""}`}
              onClick={loadMonth}
              disabled={loading}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="mat-body">
          <div className="mat-calendar-wrap">
            <CalendarView
              events={events}
              disableFutureDates
              selectedDate={selectedDate}
              onDayPress={onDayPress}
              onDayLongPress={() => {}}
              onPrevPress={handlePrevMonth}
              onNextPress={handleNextMonth}
              start_date={sessionBounds.start || "2000-01-01"}
              end_date={sessionBounds.end || formatIso(new Date()).iso}
            />
          </div>

          <div className="mat-meta">
            <div className="mat-meta__item">
              <span>Selected date</span>
              <strong>{selectedParts.display}</strong>
            </div>
            <div className="mat-meta__item">
              <span>Day status</span>
              <strong>{stats.dayStatus}</strong>
            </div>
            <div className="mat-meta__item">
              <span>Marked</span>
              <strong>
                {stats.marked} / {stats.total}
              </strong>
            </div>
          </div>

          {/^holiday$/i.test(stats.dayStatus) ? (
            <div className="mat-banner mat-banner--holiday">
              <strong>Holiday</strong>
            </div>
          ) : null}

          <div className="sm-kpi-row sm-kpi-row--six mat-kpis" aria-label="Attendance totals">
            <KpiChip tone="sky" icon={FaUsers} label="Staff" value={stats.total} />
            <KpiChip tone="mint" icon={FaCheckCircle} label="Present" value={stats.present} />
            <KpiChip tone="sand" icon={FaTimesCircle} label="Absent" value={stats.absent} />
            <KpiChip tone="blue" icon={FaCalendarAlt} label="Leave" value={stats.leave} />
            <KpiChip tone="blue" icon={FaUserClock} label="Half Day" value={stats.halfDay} />
            <KpiChip tone="sand" icon={FaClock} label="Late" value={stats.late} />
          </div>

          <section className="atd-section atd-section--last">
            <div className="atd-section__head sm-section-head">
              <div className="min-w-0">
                <h2 className="atd-section__title">Staff for {selectedParts.display}</h2>
                <p className="atd-section__hint">
                  Take or review attendance · {stats.pending} pending
                </p>
              </div>
              <label className="sm-att-staff-search">
                <FaSearch aria-hidden />
                <input
                  type="search"
                  placeholder="Search staff"
                  value={staffQuery}
                  onChange={(e) => setStaffQuery(e.target.value)}
                  aria-label="Search staff"
                />
              </label>
            </div>

            {loading ? (
              <div className="mat-loading">
                <MyLoader />
              </div>
            ) : !selectedDay ? (
              <div className="atd-empty">No staff found for this date.</div>
            ) : (
              <div className="sm-att-staff-split">
                <article className="sm-att-staff-panel sm-att-staff-panel--recorded">
                  <header className="sm-att-staff-panel__head">
                    <div className="min-w-0">
                      <h3>Attendance recorded</h3>
                      <p>{groupedStaff.recorded.length} staff marked</p>
                    </div>
                    <span className="sm-att-staff-panel__count">
                      {groupedStaff.recorded.length}
                    </span>
                  </header>
                  {groupedStaff.recorded.length === 0 ? (
                    <div className="sm-att-staff-group__empty">
                      {groupedStaff.query
                        ? "No matching recorded staff."
                        : "No attendance recorded yet."}
                    </div>
                  ) : (
                    <div className="sm-att-staff-list sm-att-staff-list--cards">
                      {groupedStaff.recorded.map((s) => renderStaffRow(s))}
                    </div>
                  )}
                </article>

                <article className="sm-att-staff-panel sm-att-staff-panel--pending">
                  <header className="sm-att-staff-panel__head sm-att-pending-head">
                    <div className="min-w-0">
                      <h3>Not recorded</h3>
                      <p>{groupedStaff.notRecorded.length} staff pending</p>
                    </div>
                    <div className="sm-att-mark-legend" aria-label="Attendance codes">
                      {MARK_CHIPS.map((chip) => (
                        <span
                          key={chip.code}
                          className={`sm-att-mark-legend__item is-${chip.code.toLowerCase()}`}
                        >
                          <em>{chip.code}</em>
                          {chip.title}
                        </span>
                      ))}
                    </div>
                    <span className="sm-att-staff-panel__count">
                      {groupedStaff.notRecorded.length}
                    </span>
                  </header>
                  {groupedStaff.notRecorded.length === 0 ? (
                    <div className="sm-att-staff-group__empty">
                      {groupedStaff.query
                        ? "No matching pending staff."
                        : "All staff attendance is recorded."}
                    </div>
                  ) : (
                    <>
                      <div className="sm-att-pending-list">
                        {groupedStaff.notRecorded.map((s, index) =>
                          renderPendingRow(s, index)
                        )}
                      </div>
                      <div className="sm-att-pending-foot">
                        <button
                          type="button"
                          className="sm-att-record-all-btn"
                          disabled={saving}
                          onClick={recordAllPending}
                        >
                          <FaClipboardCheck aria-hidden /> Record all{" "}
                          {groupedStaff.notRecorded.length}
                        </button>
                      </div>
                    </>
                  )}
                </article>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
